import { parseServerMessage } from '../../../../shared/protocol';
import type { ConnectionStatus, JoinRoomOptions } from '../../types/room';
import type { ClientEvent, ClientJoinEvent, ServerEvent } from '../../types/websocket';

export interface RoomClientOptions {
  roomId: string;
  onMessage: (event: ServerEvent) => void;
  onStatusChange: (status: ConnectionStatus) => void;
}

const MAX_RETRIES = 8;
const MAX_QUEUE_SIZE = 20;

/** WebSocket lifecycle only. Authentication is replayed on every reconnect. */
export class RoomClient {
  private ws: WebSocket | null = null;
  private joinEvent: ClientJoinEvent | null = null;
  private queue: ClientEvent[] = [];
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private retryCount = 0;
  private joined = false;
  private roomStateReceived = false;
  private stopping = false;
  private terminalFailure = false;

  constructor(private readonly options: RoomClientOptions) {}

  connect(join: JoinRoomOptions | ClientJoinEvent): void {
    this.joinEvent = 'type' in join ? join : {
      type: 'JOIN',
      roomId: join.roomId,
      nickname: join.nickname,
      password: join.password,
    };
    this.stopping = false;
    this.terminalFailure = false;
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) this.open();
  }

  send(event: ClientEvent): void {
    if (this.ws?.readyState === WebSocket.OPEN && this.joined && this.roomStateReceived) {
      this.sendRaw(event);
      return;
    }
    if (event.type === 'CHAT_MESSAGE' && this.queue.length < MAX_QUEUE_SIZE) this.queue.push(event);
  }

  disconnect(): void {
    this.stopping = true;
    this.terminalFailure = true;
    this.clearTimers();
    this.queue = [];
    this.joined = false;
    this.roomStateReceived = false;
    this.ws?.close(1000, 'Client disconnected');
    this.ws = null;
    this.options.onStatusChange('disconnected');
  }

  retryNow(): void {
    if (this.stopping) return;
    this.clearTimers();
    this.retryCount = 0;
    this.terminalFailure = false;
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) this.open();
  }

  private open(): void {
    if (this.stopping || !this.joinEvent) return;
    this.options.onStatusChange(this.retryCount === 0 ? 'connecting' : 'reconnecting');
    let socket: WebSocket;
    try {
      socket = new WebSocket(this.getWsUrl());
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = socket;
    socket.onopen = () => {
      if (this.stopping) return;
      this.joined = false;
      this.roomStateReceived = false;
      this.sendRaw(this.joinEvent as ClientJoinEvent);
      this.startPing();
    };
    socket.onmessage = (event: MessageEvent) => {
      if (typeof event.data !== 'string') return;
      const message = parseServerMessage(event.data);
      if (!message) return;
      if (message.type === 'JOIN_SUCCESS') {
        this.joined = true;
        this.retryCount = 0;
      }
      if (message.type === 'ROOM_STATE' && this.joined) {
        this.roomStateReceived = true;
        this.options.onMessage(message);
        this.options.onStatusChange('connected');
        this.flushQueue();
        return;
      }
      if (message.type === 'ERROR' && ['ROOM_NOT_FOUND', 'ROOM_FULL', 'WRONG_PASSWORD', 'PASSWORD_LOCKED', 'PASSWORD_CHANGED', 'ROOM_CLOSED'].includes(message.code)) {
        this.terminalFailure = true;
      }
      this.options.onMessage(message);
    };
    socket.onerror = () => { /* close carries the lifecycle transition */ };
    socket.onclose = () => {
      this.stopPing();
      this.ws = null;
      this.joined = false;
      this.roomStateReceived = false;
      if (this.stopping || this.terminalFailure) {
        this.options.onStatusChange('disconnected');
        return;
      }
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.stopping || this.retryCount >= MAX_RETRIES) {
      this.options.onStatusChange('disconnected');
      return;
    }
    const delay = Math.min(1000 * 2 ** this.retryCount, 30_000);
    this.retryCount += 1;
    this.options.onStatusChange('reconnecting');
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.reconnect();
    }, delay);
  }

  private async reconnect(): Promise<void> {
    if (this.stopping || !this.joinEvent) return;
    try {
      const response = await fetch(`/api/rooms/${this.options.roomId}/exists`);
      const result = response.ok ? await response.json() as { exists?: boolean } : { exists: false };
      if (!result.exists) {
        this.terminalFailure = true;
        this.options.onMessage({ type: 'ERROR', code: 'ROOM_NOT_FOUND', message: 'Sala não encontrada ou expirada.' });
        this.options.onStatusChange('disconnected');
        return;
      }
    } catch {
      // A temporary HTTP failure should not prevent the WebSocket retry.
    }
    this.open();
  }

  private flushQueue(): void {
    const queued = this.queue.splice(0);
    for (const event of queued) this.sendRaw(event);
  }

  private sendRaw(event: ClientEvent): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(event));
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => this.send({ type: 'PING' }), 30_000);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  private clearTimers(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.stopPing();
  }

  private getWsUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/room/${this.options.roomId}/ws`;
  }
}
